import React, { useState, useEffect } from 'react';
import { Plus, Edit3, Trash2, CheckCircle, AlertTriangle, Layers, Percent } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import API from '../../services/api';
import '../Dashboard.css';

const ComposantsHebergement = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const company_id = user?.company_id || user?.companyId;

  const [composants, setComposants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  // 🔥 États pour les infos de l'entreprise (TVA)
  const [companyInfo, setCompanyInfo] = useState({ regime_tva_recuperable: 1, taux_tva: 18.0 });

  const [formData, setFormData] = useState({
    libelle: '',
    prix_defaut: '',
    soumis_tva: true // Par défaut coché si applicable
  });

  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [modalConfig, setModalConfig] = useState({ show: false, action: null, message: '', id: null });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const fetchCompanyAndData = async () => {
    try {
      setLoading(true);
      // 1. Récupérer les paramètres de l'entreprise pour connaître son régime TVA et son taux
      if (company_id) {
        const compRes = await API.get(`/company/${company_id}`);
        if (compRes.data?.success && compRes.data.data) {
          const compData = compRes.data.data;
          setCompanyInfo({
            regime_tva_recuperable: compData.regime_tva_recuperable ?? 1,
            taux_tva: compData.taux_tva ?? 18.0
          });
        }
      }

      // 2. Récupérer les composants de manière sécurisée (garantie d'obtenir un tableau)
      const res = await API.get('/hebergement/composants');
      const rawData = res.data;
      let listComposants = [];
      if (Array.isArray(rawData)) {
        listComposants = rawData;
      } else if (rawData && typeof rawData === 'object') {
        const potentialArray = rawData.composants || rawData.data || rawData.items;
        if (Array.isArray(potentialArray)) {
          listComposants = potentialArray;
        }
      }
      setComposants(listComposants);
    } catch (err) {
      console.error("Erreur chargement données:", err);
      showToast("Erreur de chargement des données", "error");
      setComposants([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanyAndData();
  }, [company_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.libelle.trim()) {
      showToast("Le libellé du composant est obligatoire", "error");
      return;
    }

    // 🎯 Validation stricte : Vérification que le prix est renseigné et est un nombre valide
    const prixNumerique = parseFloat(formData.prix_defaut);
    if (formData.prix_defaut === '' || isNaN(prixNumerique) || prixNumerique < 0) {
      showToast("Le prix de la prestation doit être obligatoire et numérique", "error");
      return;
    }

    setLoading(true);
    try {
      // Si l'entreprise n'est pas assujettie (régime forfaitaire / non assujetti = 0), on force soumis_tva à false
      const isTvaApplicable = companyInfo.regime_tva_recuperable === 1 ? formData.soumis_tva : false;
      const tauxApplique = isTvaApplicable ? Number(companyInfo.taux_tva) : 0;

      const payload = { 
        libelle: formData.libelle.trim(),
        prix_defaut: prixNumerique,
        soumis_tva: isTvaApplicable ? 1 : 0,
        taux_tva: tauxApplique
      };

      if (editingId) {
        await API.put(`/hebergement/composants/${editingId}`, payload);
        showToast("Composant mis à jour avec succès");
      } else {
        await API.post('/hebergement/composants', payload);
        showToast("Composant créé avec succès");
      }

      closeForm();
      fetchCompanyAndData();
    } catch (err) {
      console.error("Erreur enregistrement composant :", err);
      const errorMsg = err.response?.data?.error || "Erreur lors de l'enregistrement";
      showToast(errorMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id) => {
    setModalConfig({
      show: true,
      action: () => executeDelete(id),
      message: "Voulez-vous vraiment supprimer ce composant ?",
      id
    });
  };

  const executeDelete = async (id) => {
    setLoading(true);
    try {
      await API.delete(`/hebergement/composants/${id}`);
      showToast("Composant supprimé avec succès");
      setModalConfig({ show: false, action: null, message: '', id: null });
      fetchCompanyAndData();
    } catch (err) {
      const errorMsg = err.response?.data?.error || "Impossible de supprimer ce composant.";
      showToast(errorMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  const closeForm = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData({ libelle: '', prix_defaut: '', soumis_tva: true });
  };

  // Vérifie si l'entreprise gère la TVA (Régime Réel = 1)
  const isEntrepriseSousTva = companyInfo.regime_tva_recuperable === 1;

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <Sidebar />
      <main style={{ flex: 1, overflowY: 'auto', background: '#f1f5f9', position: 'relative' }}>
        
        {toast.show && (
          <div style={{...toastContainerStyle, background: toast.type === 'success' ? '#10b981' : '#ef4444'}}>
            {toast.type === 'success' ? <CheckCircle size={18}/> : <AlertTriangle size={18}/>}
            {toast.message}
          </div>
        )}

        <header style={headerStyle}>
          <div>
            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Layers size={22} color="#2563eb" /> LISTE DES COMPOSANTS (PRESTATIONS)
            </h1>
            <div style={{ color: '#64748b', fontSize: '13px' }}>
              Répertoire global des éléments disponibles pour la configuration des chambres
            </div>
          </div>
          <div>
            {!isAdding && !editingId && (
              <button onClick={() => setIsAdding(true)} style={btnPrimaryStyle}>
                <Plus size={18} /> Nouveau Composant
              </button>
            )}
          </div>
        </header>

        <div style={{ padding: '30px 40px' }}>

          {(isAdding || editingId) && (
            <div style={{ ...contentCardStyle, padding: '25px', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '14px', marginBottom: '15px', fontWeight: '800' }}>
                {editingId ? 'Modifier le composant' : 'Ajouter un nouveau composant'}
              </h3>
              <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '20px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ flex: 2, minWidth: '220px' }}>
                  <label style={labelStyle}>LIBELLÉ DE LA PRESTATION *</label>
                  <input 
                    type="text" 
                    style={inputStyle} 
                    placeholder="Ex: Nuitée, Petit-déjeuner, Piscine, Spa..."
                    value={formData.libelle} 
                    onChange={(e) => setFormData({...formData, libelle: e.target.value})} 
                    required 
                  />
                </div>
                <div style={{ flex: 1, minWidth: '160px' }}>
                  <label style={labelStyle}>PRIX INDICATIF / DÉFAUT (FCFA) *</label>
                  <input 
                    type="number" 
                    step="any"
                    min="0"
                    style={inputStyle} 
                    placeholder="Ex: 5000"
                    value={formData.prix_defaut} 
                    onChange={(e) => setFormData({...formData, prix_defaut: e.target.value})} 
                    required
                  />
                </div>

                {/* 🔥 SECTION TVA CONDITIONNELLE (Grisée ou masquée si l'entreprise n'est pas assujettie) */}
                <div style={{ 
                  flex: 1.5, 
                  minWidth: '200px', 
                  opacity: isEntrepriseSousTva ? 1 : 0.5,
                  background: isEntrepriseSousTva ? '#f0fdf4' : '#f1f5f9',
                  padding: '10px 15px',
                  borderRadius: '8px',
                  border: `1px solid ${isEntrepriseSousTva ? '#bbf7d0' : '#cbd5e1'}`
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: isEntrepriseSousTva ? 'pointer' : 'not-allowed', fontWeight: '800', fontSize: '12px', color: '#166534' }}>
                    <input 
                      type="checkbox" 
                      checked={isEntrepriseSousTva ? formData.soumis_tva : false}
                      disabled={!isEntrepriseSousTva}
                      onChange={(e) => setFormData({...formData, soumis_tva: e.target.checked})}
                      style={{ width: '16px', height: '16px', cursor: isEntrepriseSousTva ? 'pointer' : 'not-allowed' }}
                    />
                    <Percent size={14}/> Soumis à la TVA ({companyInfo.taux_tva}%)
                  </label>
                  <span style={{ fontSize: '10px', color: '#64748b', display: 'block', marginTop: '4px' }}>
                    {isEntrepriseSousTva 
                      ? `Applique automatiquement ${companyInfo.taux_tva}% si coché.` 
                      : "Désactivé (Entreprise en régime forfaitaire / non assujetti)."}
                  </span>
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button type="submit" disabled={loading} style={btnSubmitStyle}>{loading ? '...' : 'Valider'}</button>
                  <button type="button" onClick={closeForm} style={btnCancelStyle}>Annuler</button>
                </div>
              </form>
            </div>
          )}

          <div style={contentCardStyle}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={thStyle}>Libellé de la prestation</th>
                  <th style={thStyle}>Prix Indicatif / Défaut</th>
                  <th style={thStyle}>Régime / TVA</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {composants.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                      Aucun composant enregistré.
                    </td>
                  </tr>
                ) : (
                  composants.map((item) => (
                    <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ ...tdStyle, fontWeight: '800' }}>
                        {item.libelle || item.nom}
                      </td>
                      <td style={{ ...tdStyle, color: '#059669', fontWeight: '800' }}>
                        {Number(item.prix_defaut || 0).toLocaleString()} F
                      </td>
                      <td style={tdStyle}>
                        {item.soumis_tva ? (
                          <span style={{ background: '#dcfce7', color: '#15803d', padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '800' }}>
                            TVA Appliquée ({item.taux_tva || companyInfo.taux_tva}%)
                          </span>
                        ) : (
                          <span style={{ background: '#f1f5f9', color: '#64748b', padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '700' }}>
                            Non soumis / Forfaitaire
                          </span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                          <button 
                            onClick={() => {
                              setEditingId(item.id); 
                              setFormData({
                                libelle: item.libelle || item.nom,
                                prix_defaut: item.prix_defaut || 0,
                                soumis_tva: item.soumis_tva === 1 || item.soumis_tva === true
                              }); 
                              setIsAdding(false);
                            }} 
                            style={actionBtnStyle('#2563eb')}
                          >
                            <Edit3 size={14}/> Modifier
                          </button>
                          
                          <button onClick={() => handleDeleteClick(item.id)} style={actionBtnStyle('#ef4444')}>
                            <Trash2 size={14}/> Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {modalConfig.show && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
              <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px', borderRadius: '50%' }}><AlertTriangle size={24} /></div>
              <h2 style={{ margin: 0, fontSize: '18px' }}>Confirmation de suppression</h2>
            </div>
            <p style={{ color: '#475569', marginBottom: '30px' }}>{modalConfig.message}</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setModalConfig({ show: false, action: null, message: '', id: null })} style={btnSecondaryStyle}>Annuler</button>
              <button onClick={modalConfig.action} style={btnDangerStyle}>
                {loading ? '...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes slideInToast { from { transform: translate(-50%, -20px); opacity: 0; } to { transform: translate(-50%, 0); opacity: 1; } }`}</style>
    </div>
  );
};

// --- STYLES ---
const headerStyle = { background: 'white', padding: '20px 40px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const contentCardStyle = { background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', overflow: 'hidden' };
const btnPrimaryStyle = { background: '#2563eb', color: 'white', border: 'none', padding: '10px 22px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '700' };
const btnSecondaryStyle = { border: '1px solid #e2e8f0', padding: '10px 15px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#475569', background: 'white' };
const inputStyle = { width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '14px', marginTop: '5px', background: '#fff' };
const labelStyle = { fontSize: '11px', fontWeight: '900', color: '#475569' };
const thStyle = { padding: '15px 20px', fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: '800', textAlign: 'left' };
const tdStyle = { padding: '12px 20px', fontSize: '13px', color: '#334155' };
const btnSubmitStyle = { padding: '0 25px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', height: '45px' };
const btnCancelStyle = { padding: '0 20px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', height: '45px' };
const actionBtnStyle = (color) => ({ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px', borderRadius: '8px', border: `1px solid ${color}30`, background: `${color}10`, color: color, fontSize: '11px', fontWeight: '900', cursor: 'pointer' });

const toastContainerStyle = { position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 9999, color: 'white', padding: '12px 24px', borderRadius: '12px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2)', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '800', animation: 'slideInToast 0.3s ease-out' };

const modalOverlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalContentStyle = { background: 'white', padding: '30px', borderRadius: '12px', width: '400px', maxWidth: '90%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' };
const btnDangerStyle = { background: '#ef4444', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700' };

export default ComposantsHebergement;